import { FC, useCallback, useState } from 'react';
import { Alert, Button, Checkbox, Space, Typography } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { StyledCodeGrid } from './styled';

const { Title, Paragraph } = Typography;

interface IProps {
  codes: string[];
  onDone: () => void;
}

/**
 * The ONLY time these codes exist in readable form. The backend stores
 * hashes, so nothing can show them again — hence the explicit
 * confirmation before the admin can leave, and the download.
 */
const RecoveryCodes: FC<IProps> = ({ codes, onDone }) => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);

  const onCopy = useCallback(() => {
    navigator.clipboard?.writeText(codes.join('\n'));
  }, [codes]);

  const onDownload = useCallback(() => {
    // Built in the browser: the codes never reach our server again, so
    // there is nothing to download FROM.
    const blob = new Blob([codes.join('\n')], {
      type: 'text/plain;charset=utf-8'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'medicusunion-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [codes]);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <Title level={4}>{t('profile.2fa.recovery.title')}</Title>
        <Paragraph>{t('profile.2fa.recovery.description')}</Paragraph>
      </div>

      <StyledCodeGrid data-test-id="2fa-recovery-codes">
        {codes.map(code => (
          <li key={code}>{code}</li>
        ))}
      </StyledCodeGrid>

      <Alert
        type="warning"
        showIcon
        message={t('profile.2fa.recovery.warning')}
      />

      <Space wrap>
        <Button icon={<CopyOutlined />} onClick={onCopy}>
          {t('profile.2fa.recovery.copy')}
        </Button>
        <Button icon={<DownloadOutlined />} onClick={onDownload}>
          {t('profile.2fa.recovery.download')}
        </Button>
      </Space>

      <Checkbox
        checked={saved}
        onChange={e => setSaved(e.target.checked)}
        data-test-id="recovery-codes-saved-checkbox"
      >
        {t('profile.2fa.recovery.confirm')}
      </Checkbox>

      <Button
        block
        type="primary"
        disabled={!saved}
        onClick={onDone}
        data-test-id="recovery-codes-done-button"
      >
        {t('profile.2fa.recovery.done')}
      </Button>
    </Space>
  );
};

export default RecoveryCodes;
